import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';

function fmt(dateStr) {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function PromptEditor() {
  const { name } = useParams();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const [expanded, setExpanded] = useState(null);

  const active = history.find((v) => v.isActive) ?? history[0] ?? null;
  const unchanged = active !== null && content === active.content;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/dev/prompts/${encodeURIComponent(name)}`);
      setHistory(data);
      const a = data.find((v) => v.isActive) ?? data[0];
      if (a) setContent(a.content);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [name]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/dev/prompts/${encodeURIComponent(name)}`, {
        content,
        notes: notes.trim() || undefined,
      });
      setNotes('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async (version) => {
    if (!window.confirm(`Rollback to v${version}?`)) return;
    setError(null);
    try {
      await api.post(`/dev/prompts/${encodeURIComponent(name)}/rollback/${version}`);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
        <p className="text-hxd-muted text-[13px]">Loading…</p>
      </div>
    );
  }

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 py-8">
      <Link
        to="/admin/dev/prompts"
        className="text-[12px] text-hxd-muted hover:text-hxd-orange transition-colors"
      >
        ← Back to prompts
      </Link>

      {error && (
        <div className="mt-4 flex items-start justify-between gap-3 bg-red-50 border border-red-200 rounded p-3">
          <p className="text-hxd-err text-[13px]">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-hxd-muted hover:text-hxd-err text-lg leading-none flex-shrink-0"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
        {/* Editor panel */}
        <div className="hxd-card">
          <div className="hxd-card-header">Editor — {name}</div>

          {active && (
            <p className="text-[12px] text-hxd-muted mb-4">
              Active: v{active.version} &middot; Updated {fmt(active.updatedAt)}
            </p>
          )}

          <div className="mb-3">
            <label className="hxd-label">Content</label>
            <textarea
              className="hxd-input font-mono text-[13px]"
              rows={16}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          <div className="mb-5">
            <label className="hxd-label">
              Notes for this version{' '}
              <span className="font-normal normal-case tracking-normal text-hxd-muted">
                (optional)
              </span>
            </label>
            <input
              className="hxd-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed?"
            />
          </div>

          <button
            className="hxd-btn hxd-btn-primary hxd-btn-sm"
            onClick={handleSave}
            disabled={unchanged || saving || !content.trim()}
          >
            {saving ? 'Saving…' : 'Save new version'}
          </button>
        </div>

        {/* Version history panel */}
        <div className="hxd-card">
          <div className="hxd-card-header">Version History</div>

          {history.length === 0 ? (
            <p className="text-hxd-muted text-[13px]">No versions yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((v) => (
                <div key={v.version} className="border border-hxd-border rounded-[3px]">
                  <button
                    type="button"
                    className="w-full flex items-start justify-between gap-2 px-3 py-2.5 hover:bg-hxd-surface transition-colors text-left"
                    onClick={() => setExpanded(expanded === v.version ? null : v.version)}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-[11px] font-bold text-hxd-ink">
                          v{v.version}
                        </span>
                        {v.isActive && (
                          <span className="font-display text-[9px] font-bold uppercase tracking-display-sm bg-hxd-orange text-white px-1.5 py-0.5 rounded-[2px]">
                            Active
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-hxd-muted mt-0.5">{fmt(v.createdAt)}</div>
                      {v.notes && (
                        <div className="text-[11px] text-hxd-muted mt-0.5 truncate">{v.notes}</div>
                      )}
                    </div>
                    <span className="text-hxd-subtle text-[10px] flex-shrink-0 mt-1">
                      {expanded === v.version ? '▲' : '▼'}
                    </span>
                  </button>

                  {expanded === v.version && (
                    <div className="border-t border-hxd-border px-3 py-2.5 bg-hxd-surface">
                      <pre className="text-[12px] font-mono whitespace-pre-wrap break-words text-hxd-ink max-h-48 overflow-y-auto">
                        {v.content}
                      </pre>
                      {!v.isActive && (
                        <button
                          type="button"
                          className="hxd-btn hxd-btn-ghost hxd-btn-sm mt-3"
                          onClick={() => handleRollback(v.version)}
                        >
                          Rollback to v{v.version}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
